import { Schema } from "effect";

/** Roles that can be issued to an authenticated Landpad staff identity. */
export const StaffRoleSchema = Schema.Literal("admin", "kitchen-staff");

/** An authenticated Landpad staff role. */
export type StaffRole = typeof StaffRoleSchema.Type;

/** Languages available for the Landpad staff interface. */
export const StaffLanguageSchema = Schema.Literal("en", "ja");

/** A saved Landpad staff interface language. */
export type StaffLanguage = typeof StaffLanguageSchema.Type;

const StaffAccessTokenBaseClaims = {
  exp: Schema.Number.pipe(Schema.positive()),
  iat: Schema.Number.pipe(Schema.nonNegative()),
  sub: Schema.NonEmptyString,
  userId: Schema.NonEmptyString,
  username: Schema.NonEmptyString,
  language: StaffLanguageSchema,
} as const;

/** A normal staff session established with that user's own credential. */
export const DirectStaffAccessTokenClaimsSchema = Schema.Struct({
  ...StaffAccessTokenBaseClaims,
  sessionKind: Schema.Literal("direct"),
  role: StaffRoleSchema,
});

/** An administrator-issued kitchen shift with an independent hard expiry. */
export const KitchenShiftAccessTokenClaimsSchema = Schema.Struct({
  ...StaffAccessTokenBaseClaims,
  sessionKind: Schema.Literal("kitchen-shift"),
  role: Schema.Literal("kitchen-staff"),
  issuedBy: Schema.NonEmptyString,
  shiftExpiresAt: Schema.Number.pipe(Schema.positive()),
});

/** Signed claims issued by Better Auth for Landpad API and realtime access. */
export const StaffAccessTokenClaimsSchema = Schema.Union(
  DirectStaffAccessTokenClaimsSchema,
  KitchenShiftAccessTokenClaimsSchema,
);

/** Signed Better Auth claims accepted by Landpad. */
export type StaffAccessTokenClaims =
  typeof StaffAccessTokenClaimsSchema.Type;

/** Apply the shorter JWT or delegated-shift boundary. */
export function effectiveStaffAccessExpiry(
  claims: StaffAccessTokenClaims,
): number {
  return claims.sessionKind === "kitchen-shift"
    ? Math.min(claims.exp, claims.shiftExpiresAt)
    : claims.exp;
}

/** Username accepted for a credential-free kitchen staff identity. */
export const KitchenStaffUsernameSchema = Schema.Trim.pipe(
  Schema.minLength(3),
  Schema.maxLength(64),
  Schema.pattern(/^[A-Za-z0-9._-]+$/),
);

/** Public kitchen identity metadata; storage-only email is intentionally absent. */
export const KitchenStaffAccountSchema = Schema.Struct({
  id: Schema.NonEmptyString,
  username: KitchenStaffUsernameSchema,
  role: Schema.Literal("kitchen-staff"),
});

/** Kitchen identities visible to an administrator. */
export const KitchenStaffAccountListSchema = Schema.Array(
  KitchenStaffAccountSchema,
);

/** Create a credential-free kitchen identity from its visible username. */
export const CreateKitchenStaffRequestSchema = Schema.Struct({
  username: KitchenStaffUsernameSchema,
});

/** Path parameters identifying a kitchen staff account. */
export const KitchenStaffAccountParamsSchema = Schema.Struct({
  userId: Schema.NonEmptyString,
});

/** Request an administrator-issued session for one kitchen identity. */
export const CreateKitchenShiftRequestSchema = Schema.Struct({
  userId: Schema.NonEmptyString,
});

/** One-use browser handoff metadata; the token must remain redacted in logs. */
export const CreateKitchenShiftResponseSchema = Schema.Struct({
  token: Schema.NonEmptyString,
  handoffExpiresAt: Schema.Number.pipe(Schema.positive()),
  shiftExpiresAt: Schema.Number.pipe(Schema.positive()),
});

/** Whether a role may operate the live kitchen order surface. */
export function canOperateKitchen(role: StaffRole): boolean {
  return role === "admin" || role === "kitchen-staff";
}

/** Whether a role may access administrative Landpad capabilities. */
export function canAdministerLandpad(role: StaffRole): boolean {
  return role === "admin";
}

export const PostAuthRegister200Schema = Schema.Struct({
  message: Schema.String,
  userId: Schema.String,
});
export const PostAuthLogin200Schema = Schema.Struct({
  userId: Schema.String,
  username: Schema.String,
  statements: Schema.Array(
    Schema.Struct({
      effect: Schema.Literal("allow", "deny"),
      action: Schema.Array(Schema.String),
      resource: Schema.String,
    }),
  ),
  accessToken: Schema.String,
});
export const PostAuthRefresh200Schema = Schema.Struct({
  accessToken: Schema.String,
});
export const PostAuthLogout200Schema = Schema.Struct({
  message: Schema.String,
});
